import {ApiUrls} from 'shared/config/model/urls';

import {SubmitHandler, useForm, UseFormReturn} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';
import {t} from '@lingui/core/macro';

import {httpMonitorAPI} from 'features/http/api/httpMonitorAPI';
import {buildHttpBatchCreatePayload, getHttpBatchCreateDefaults} from 'features/http/lib/httpMonitorFormHelper';
import CreateHttpMonitorType from 'features/http/model/HttpMonitor/CreateHttpMonitorType';
import {useBatchOperation, useBatchResult, IBatchResult, invalidateDomainMonitors} from 'entities/monitor';
import {useIsFreeTariff, userSelector} from 'entities/user';
import {TypedDispatch} from 'shared/model/types';
import {isTranslate} from 'shared/lib/lang';

import {useHeadersServerError} from './useHeadersServerError';

interface IUseHttpMonitorBatchCreateFormParams {
    handleClose: () => void,
    // eslint-disable-next-line no-unused-vars
    onShowResultChange?: (isShowing: boolean) => void,
}

export interface IUseHttpMonitorBatchCreateForm {
    form: UseFormReturn<CreateHttpMonitorType>,
    result: IBatchResult | undefined,
    isLoading: boolean,
    progress: number,
    freeTariff: boolean,
    submitText: string,
    resultTitle: string,
    submit: ReturnType<UseFormReturn<CreateHttpMonitorType>['handleSubmit']>,
    handleCloseResult: () => void,
}

export const useHttpMonitorBatchCreateForm = ({
    handleClose,
    onShowResultChange,
}: IUseHttpMonitorBatchCreateFormParams): IUseHttpMonitorBatchCreateForm => {
    const dispatch = useDispatch<TypedDispatch>();
    const user = useSelector(userSelector);
    const {result, showResult, resetResult} = useBatchResult(onShowResultChange);
    const {trigger, isLoading, progress} = useBatchOperation();
    const freeTariff = useIsFreeTariff();

    const form = useForm<CreateHttpMonitorType>({
        mode: 'onChange',
        defaultValues: getHttpBatchCreateDefaults(user, freeTariff, isTranslate()),
    });
    const handleSubmitError = useHeadersServerError(form);

    const onSubmit: SubmitHandler<CreateHttpMonitorType> = async(values) => {
        try {
            await trigger(
                ApiUrls.HTTP_MONITORS_BATCH_ACTION('create'),
                'POST',
                buildHttpBatchCreatePayload(values),
                (batchResult) => {
                    dispatch(httpMonitorAPI.util.invalidateTags([
                        {type: 'HttpMonitor', id: 'LIST'},
                        'SummaryHttpMonitors',
                    ]));
                    invalidateDomainMonitors(dispatch);
                    showResult(batchResult);
                },
                {silent: true},
            );
        } catch (error) {
            handleSubmitError(error);
        }
    };

    const handleCloseResult = () => {
        handleClose();
        resetResult();
    };

    return {
        form,
        result,
        isLoading,
        progress,
        freeTariff,
        submitText: t`Добавить`,
        resultTitle: t`Сайты добавлены`,
        submit: form.handleSubmit(onSubmit),
        handleCloseResult,
    };
};

export default useHttpMonitorBatchCreateForm;
